import { AfterViewInit, Component, Input, NgZone, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { EidAuthService } from '@app/auth/services/eid-auth.service';
import { NeoTopNavbarConfig, NeoTopNavbarProfileMenuItem } from '@neogov/top-navbar-definition';
import { LayoutService, RouteHelperService, TokenStorageService, TopNavBarService } from '@shared/services';
import { Subscription } from 'rxjs';
import { HTMLElementTagNameMap } from '@shared/types';
import { NavNotificationsService } from '@api/api-services/nav-notifications.service';
import { ProfileService } from '@app/api/api-services/profile.service';

@Component({
  selector: 'fto-top-nav-bar',
  templateUrl: './top-nav-bar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TopNavBarComponent implements AfterViewInit, OnDestroy {
  navbar?: HTMLElementTagNameMap['ce-neogov-top-navbar'];
  private readonly subscriptions: Subscription = new Subscription();
  private themeSetupInterval: number | null = null;

  @Input()
  theme: string;

  constructor(
    private readonly topnavBarService: TopNavBarService,
    private readonly _ngZone: NgZone,
    private readonly routeHelper: RouteHelperService,
    private readonly layoutService: LayoutService,
    private readonly tokenStorageService: TokenStorageService,
    private readonly eidAuthService: EidAuthService,
    private readonly navNotificationsService: NavNotificationsService,
    private readonly profileService: ProfileService
  ) {}

  ngAfterViewInit(): void {
    this.navbar = document.querySelector('ce-neogov-top-navbar');

    if (this.navbar) {
      this.setupNavbar();

      this.setUpTheme(this.theme);
    }

    this.subscriptions.add(
      this.profileService.themePreferenceChanged$.subscribe((isNightMode: boolean) => {
        if (this.navbar) {
          this.setUpTheme(isNightMode ? 'dark' : 'light');
        }
      })
    );

    this.subscriptions.add(
      this.profileService.getThemePreference().subscribe({
        next: (isNightMode: boolean) => {
          if (this.navbar) {
            this.setUpTheme(isNightMode ? 'dark' : 'light');
          } else {
            this.themeSetupInterval = window.setInterval(() => {
              if (this.navbar) {
                this.setUpTheme(isNightMode ? 'dark' : 'light');
                if (this.themeSetupInterval !== null) {
                  clearInterval(this.themeSetupInterval);
                  this.themeSetupInterval = null;
                }
              }
            }, 100);
          }
        }
      })
    );
  }

  setupNavbar(): void {
    this.navbar.ngZone = this._ngZone;
    this.navbar.productName = 'Ready';

    this.setupProfile();
    this.setHelp();
    this.setUpAppSwitcher();
    this.setUpTheme();
    if (this.tokenStorageService.userInfo?.IsSysAdmin || this.tokenStorageService.userInfo?.IsSiteAdmin) {
      this.setUpSettings();
    }

    this.subscribeToUserInfoChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.themeSetupInterval !== null) {
      clearInterval(this.themeSetupInterval);
      this.themeSetupInterval = null;
    }
  }

  private setUpSettings() {
    const config: Partial<NeoTopNavbarConfig['settings']> = {
      isEnabled: true,
      items: [
        ...(this.tokenStorageService.userInfo?.IsSiteAdmin ||
        (this.tokenStorageService.userInfo?.IsSysAdmin && this.tokenStorageService.userInfo?.CurrentSiteId)
          ? [
              {
                id: 'siteDashboard',
                title: 'Site Dashboard',
                clickable: {
                  type: 'button' as const,
                  onClick: () => {
                    this.routeHelper.siteDashboard();
                    return {};
                  }
                }
              },
              {
                id: 'userManagement',
                title: 'User Management',
                clickable: {
                  type: 'button' as const,
                  onClick: () => {
                    this.routeHelper.users();
                    return {};
                  }
                }
              },
              {
                id: 'programs',
                title: 'Programs',
                clickable: {
                  type: 'button' as const,
                  onClick: () => {
                    this.routeHelper.programs();
                    return {};
                  }
                }
              },
              {
                id: 'siteLists',
                title: 'Site Lists',
                clickable: {
                  type: 'button' as const,
                  onClick: () => {
                    this.routeHelper.siteLists();
                    return {};
                  }
                }
              },
              {
                id: 'addOns',
                title: 'Add-Ons',
                clickable: {
                  type: 'button' as const,
                  onClick: () => {
                    this.routeHelper.addOns();
                    return {};
                  }
                }
              },
              {
                id: 'agencyInformation',
                title: 'Agency Information',
                clickable: {
                  type: 'button' as const,
                  onClick: () => {
                    this.routeHelper.agencyInformation();
                    return {};
                  }
                }
              },
              {
                id: 'auditLog',
                title: 'Audit Log',
                clickable: {
                  type: 'button' as const,
                  onClick: () => {
                    this.routeHelper.auditLog();
                    return {};
                  }
                }
              }
            ]
          : []),
        ...(this.tokenStorageService.userInfo?.IsSysAdmin
          ? [
              {
                id: 'systemDashboard',
                title: 'System Dashboard',
                clickable: {
                  type: 'button' as const,
                  onClick: () => {
                    this.routeHelper.systemDashboard();
                    return {};
                  }
                }
              },
              {
                id: 'sites',
                title: 'Sites',
                clickable: {
                  type: 'button' as const,
                  onClick: () => {
                    this.routeHelper.sites();
                    return {};
                  }
                }
              },
              {
                id: 'templates',
                title: 'Templates',
                clickable: {
                  type: 'button' as const,
                  onClick: () => {
                    this.routeHelper.templates();
                    return {};
                  }
                }
              },
              {
                id: 'superUser',
                title: 'Super User',
                clickable: {
                  type: 'button' as const,
                  onClick: () => {
                    this.routeHelper.superUsers();
                    return {};
                  }
                }
              },
              {
                id: 'backgroundServices',
                title: 'Background Services',
                clickable: {
                  type: 'button' as const,
                  onClick: () => {
                    this.routeHelper.backgroundServices();
                    return {};
                  }
                }
              }
            ]
          : [])
      ]
    };

    this.navbar.settings.setSettings(config);
  }

  private setUpAppSwitcher() {
    const subscription = this.eidAuthService.accessToken.subscribe((token) => {
      if (token) {
        this.topnavBarService.getAppSwitcherOptions(token).subscribe((options) => {
          this.navbar.appSwitcher.setAppSwitcher({
            isEnabled: true,
            options: options
          });
        });
      }
    });
    this.subscriptions.add(subscription);
  }

  private setupProfile() {
    const profileMenuItems: NeoTopNavbarProfileMenuItem[] = [
      {
        label: 'My Profile',
        clickable: {
          type: 'anchor',
          anchorHref: '/profile',
          onClick: (e) => {
            this.topnavBarService.myProfile(this.tokenStorageService.userInfo.UserId);
            e.preventDefault();
            return { preventOverlayClose: false };
          }
        }
      },
      {
        label: 'Sign Out',
        clickable: {
          type: 'button',
          onClick: (e) => {
            this.topnavBarService.logout();
            e.preventDefault();
            return {};
          }
        }
      }
    ];

    const currentUser = this.tokenStorageService.userInfo;

    this.navbar.profile.setProfile({
      avatar: {
        firstName: currentUser.UserFirstname,
        lastName: currentUser.UserSurname,
        photoUrl: currentUser.AvatarSrc
      },
      profileMenuItems
    });
  }

  private subscribeToUserInfoChanges(): void {
    const subscription = this.tokenStorageService.userInfoChanged.subscribe(() => {
      this.setupProfile();
    });

    this.subscriptions.add(subscription);
  }

  private setHelp(): void {
    const config: Partial<NeoTopNavbarConfig['help']> = {
      isEnabled: true,
      items: [
        {
          id: 'powerdms-university',
          title: 'PowerDMS University',
          clickable: {
            anchorTarget: '_blank',
            type: 'anchor',
            anchorHref: 'https://www.powerdmsuniversity.com/',
            onClick: () => {
              return {};
            }
          }
        },
        {
          id: 'success-center',
          title: 'Success community',
          clickable: {
            anchorTarget: '_blank',
            type: 'anchor',
            anchorHref: 'https://success.neogov.com',
            onClick: () => {
              return {};
            }
          }
        }
      ]
    };

    this.navbar.help.setHelp(config);
  }

  private setUpTheme(theme = 'light'): void {
    this.navbar.theme.setTheme({
      isEnabled: true,
      theme: theme,
      onThemeChange: (changedTheme: 'light' | 'dark') => {
        this.navNotificationsService.closeNotifications();
        if (changedTheme === 'light') {
          this.layoutService.enableDayMode(true);
        } else {
          this.layoutService.enableNightMode(true);
        }
      }
    });
  }
}
